import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import BulkUploadCard from "./BulkUploadCard";
import StockRegisterImport from "./StockRegisterImport";
import StockIssueReconciliation from "./StockIssueReconciliation";
import { useSelector } from "react-redux";
import { selectUser, selectToken } from "../../../../store/authSlice";
import { canImportStockRegister } from "./stockRegisterAccess";
import {
  bulkCreateSkusApi,
  bulkStoreEntriesApi,
} from "../../../../services/inventory";

// Pop-up wrappers around BulkUploadCard, shared by the Inventory pages.

const Shell = ({ show, onHide, title, children, wide }) => (
  <Modal show={show} onHide={onHide} centered size={wide ? "xl" : "lg"}>
    <Modal.Header closeButton>
      <Modal.Title as="h5">{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-0">{children}</Modal.Body>
  </Modal>
);

export const SkuBulkUploadModal = ({ show, onHide, onDone }) => (
  <Shell show={show} onHide={onHide} title="Bulk Upload SKUs">
    <BulkUploadCard
      title="Upload Excel file"
      description="Two columns only: Item Name and Measurement (e.g. kg, pcs, mtr, ltr). Items that already exist are skipped."
      templateName="sku-upload-template.xlsx"
      columns={[
        { key: "skuName", header: "Item Name", aliases: ["item", "sku", "sku name"], required: true },
        { key: "measurement", header: "Measurement", aliases: ["unit", "measurement unit"], required: true },
      ]}
      sampleRows={[
        { skuName: "Cement", measurement: "kg" },
        { skuName: "Steel Rod", measurement: "pcs" },
      ]}
      onSubmit={async (rows) => (await bulkCreateSkusApi(rows)).data}
      onDone={onDone}
    />
  </Shell>
);

// Super-admin only — callers must not render the trigger for anyone else
// (the server enforces it as well).
export const StockBulkUploadModal = ({ show, onHide, onDone }) => {
  const [tab, setTab] = useState("register");
  // the register import is for one user only — everyone else just sees the simple template
  const allowRegister = canImportStockRegister(useSelector(selectUser), useSelector(selectToken));
  if (!allowRegister) {
    return (
      <Shell show={show} onHide={onHide} title="Bulk Upload Stock (Received / Issued)" wide>
        <StockSimpleUpload onDone={onDone} />
      </Shell>
    );
  }
  return (
    <Shell show={show} onHide={onHide} title="Bulk Upload Stock" wide>
      <div className="px-3 pt-2">
        <ul className="nav nav-pills mb-2">
          {[
            ["register", "Stock register (your sheet)"],
            ["simple", "Simple template (Add / Issue rows)"],
          ].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link py-1 ${tab === key ? "active" : ""}`}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {tab === "register" ? (
        <StockRegisterImport onDone={onDone} />
      ) : (
        <StockSimpleUpload onDone={onDone} />
      )}
    </Shell>
  );
};

const itemCol = { key: "skuName", header: "Item Name", aliases: ["item", "sku"], required: true };
const dateCol = { key: "date", header: "Date", aliases: ["entry date"], required: true };
const qtyCol = { key: "qty", header: "Qty", aliases: ["quantity"], required: true };
const remarksCol = { key: "remarks", header: "Remarks", aliases: ["remark", "note", "notes"] };

const STOCK_UPLOADS = {
  add: {
    label: "Received",
    description:
      "Item Name, Date (DD-MM-YYYY), Bill No and Qty are required; Supplier Name and Remarks are optional. Items and suppliers must already exist. Past dates are allowed — balances are recalculated in date order. Rows already entered in stock (same date, bill no, supplier and qty) are skipped. If any other row is wrong, nothing is saved.",
    templateName: "stock-received-upload-template.xlsx",
    columns: [
      itemCol,
      dateCol,
      { key: "supplier", header: "Supplier Name", aliases: ["supplier", "vendor"] },
      { key: "invoiceNo", header: "Bill No", aliases: ["bill", "bill number", "invoice no", "invoice"], required: true },
      qtyCol,
      remarksCol,
    ],
    sampleRows: [
      { skuName: "Cement", date: "01-10-2026", supplier: "ABC Traders", invoiceNo: "B-101", qty: 100, remarks: "" },
      { skuName: "Bricks", date: "02-10-2026", supplier: "", invoiceNo: "B-102", qty: 4000, remarks: "Local purchase" },
    ],
  },
  issued: {
    label: "Issued",
    description:
      "Item Name, Date (DD-MM-YYYY), Voucher No and Qty are required; Contractor Name and Remarks are optional. Items and contractors must already exist. After upload you see a reconciliation per item — nothing is saved until you press Update for that item. Rows already entered in stock (same date, voucher no, contractor and qty) are skipped.",
    templateName: "stock-issued-upload-template.xlsx",
    columns: [
      itemCol,
      dateCol,
      { key: "issuedTo", header: "Contractor Name", aliases: ["contractor", "issued to"] },
      { key: "invoiceNo", header: "Voucher No", aliases: ["voucher", "voucher number"], required: true },
      qtyCol,
      remarksCol,
    ],
    sampleRows: [
      { skuName: "Cement", date: "03-10-2026", issuedTo: "Sharma Contractors", invoiceNo: "V-201", qty: 20, remarks: "" },
      { skuName: "Bricks", date: "04-10-2026", issuedTo: "", invoiceNo: "V-202", qty: 1000, remarks: "Boundary wall" },
    ],
  },
};

const StockSimpleUpload = ({ onDone }) => {
  const [type, setType] = useState("add");
  const cfg = STOCK_UPLOADS[type];
  const withType = (rows) => rows.map((r) => ({ ...r, entryType: type }));
  return (
    <>
      <div className="px-3 pt-2">
        <ul className="nav nav-tabs mb-0">
          {Object.entries(STOCK_UPLOADS).map(([key, c]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link py-1 ${type === key ? "active" : ""}`}
                onClick={() => setType(key)}
              >
                {c.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <BulkUploadCard
        key={type}
        title={`Upload ${cfg.label} Excel file`}
        description={cfg.description}
        templateName={cfg.templateName}
        columns={cfg.columns}
        sampleRows={cfg.sampleRows}
        onSubmit={async (rows) =>
          (await bulkStoreEntriesApi(withType(rows), type === "issued" ? { mode: "preview" } : {})).data
        }
        renderResult={(data, rows) => (
          <StockIssueReconciliation rows={withType(rows)} data={data} onDone={onDone} />
        )}
        onDone={onDone}
      />
    </>
  );
};
